import React from "react";
import { useForm, useFormState } from "react-hook-form";

import { DateTime } from "luxon";

import Edit from "@mui/icons-material/Edit";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";

import arrayIncludesAny from "../../shared/arrayIncludesAny.js";
import axios from "../axiosClient.js";
import useAuth0Roles from "../hooks/auth0Roles.jsx";
import useAlertSnackbar from "../hooks/useAlertSnackbar.jsx";
import Alert from "./common/Alert.jsx";
import CancelButton from "./common/CancelButton.jsx";
import DialogTitleBar from "./common/DialogTitleBar.jsx";
import FormAutocompleteInput from "./common/FormAutocompleteInput.jsx";
import FormDatePicker from "./common/FormDatePicker.jsx";
import FormStringInput from "./common/FormStringInput.jsx";
import ConfirmAction from "./ConfirmAction.jsx";

const statusOptions = [
  { name: "ICR Approved", id: "ICR Approved" },
  { name: "In Development", id: "In Development" },
  { name: "On Hold", id: "On Hold" },
  { name: "Canceled", id: "Canceled" },
  { name: "Released", id: "Released" },
];

function EngineeringProjectForm({
  // Props
  project,
  mutate,
}) {
  // This component doesn't care about the current state of the success message
  const [, setSuccess] = useAlertSnackbar("success", 30_000);

  const formTitle = React.useMemo(() => (project?.id ? "Update Project" : "Add Project"), [project?.id]);
  const disablePropagation = React.useCallback((event) => {
    event.stopPropagation();
  }, []);

  //---------------------------------------------------------------------------
  // Modal state management
  //---------------------------------------------------------------------------
  const [open, setOpen] = React.useState(false);

  //---------------------------------------------------------------------------
  // Error alerting state management
  //---------------------------------------------------------------------------
  const [error, setError] = React.useState(null);

  //---------------------------------------------------------------------------
  // Update button state management
  //---------------------------------------------------------------------------
  const [loading, setLoading] = React.useState(false);

  //---------------------------------------------------------------------------
  // Form Submission
  //---------------------------------------------------------------------------
  const today = React.useMemo(() => DateTime.now(), []);
  const defaultValues = React.useMemo(
    () => ({
      name: project?.name || "",
      jiraId: project?.jiraId || "",
      gl: project?.gl || "",
      status: statusOptions.find((option) => option.id === project?.status) || statusOptions[0],
      budget: project?.budget ?? "",
      startDate: project?.startDate ? DateTime.fromISO(project.startDate) : today,
      endDate: project?.endDate ? DateTime.fromISO(project.endDate) : today.plus({ years: 1 }),
    }),
    [project, today]
  );

  const { handleSubmit, control, reset } = useForm({
    defaultValues,
    shouldUnregister: true,
  });

  const { isDirty } = useFormState({ control });

  const onSubmit = React.useCallback(
    async (data) => {
      // Clean up the form data
      if (!["", null, undefined].includes(data.budget)) {
        data.budget = Number(data.budget);
      } else {
        data.budget = undefined;
      }
      if (data.startDate) {
        data.startDate = data.startDate.toISODate();
      } else {
        data.startDate = undefined;
      }
      if (data.endDate) {
        data.endDate = data.endDate.toISODate();
      } else {
        data.endDate = undefined;
      }
      if (data.status?.id) {
        data.status = data.status.id;
      } else {
        data.status = undefined;
      }

      // Disable the submit button and display the spinner
      setLoading(true);

      try {
        if (project) {
          await axios({
            method: "PATCH",
            url: `/api/engineering-projects/${project.id}`,
            data,
          });
        } else {
          await axios({
            method: "POST",
            url: "/api/engineering-projects",
            data,
          });
        }

        // Close the modal
        setOpen(false);

        // Update the reload state to trigger a data re-fetch
        if (mutate) {
          mutate();
        }

        setSuccess("Successfully saved changes!");
      } catch (err) {
        setError(err?.response?.data?.error || err?.message);
      }
      setLoading(false);
    },
    [project, mutate, setSuccess]
  );

  const handleOpen = React.useCallback(
    (event) => {
      event.stopPropagation();
      reset(defaultValues);
      setOpen(true);
    },
    [defaultValues, reset]
  );
  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  //---------------------------------------------------------------------------
  // Delete Confirmation Form
  //---------------------------------------------------------------------------
  const { roles } = useAuth0Roles();
  const canDelete = React.useMemo(
    () => arrayIncludesAny(roles, ["Scorecard Admin", "Scorecard PLC Admin"]),
    [roles]
  );
  const handleDeleteConfirmation = React.useCallback(async () => {
    if (project) {
      await axios({
        method: "DELETE",
        url: `/api/engineering-projects/${project.id}`,
      });

      // Reset and close the modal
      handleClose();

      // Update the reload state to trigger a data re-fetch
      if (mutate) {
        mutate();
      }
    }
  }, [handleClose, project, mutate]);

  //---------------------------------------------------------------------------
  // Render
  //---------------------------------------------------------------------------
  return (
    <>
      {project?.id ? (
        <IconButton
          color="primary"
          onClick={handleOpen}
          sx={{ display: defaultValues.disabled ? "none" : "block" }}
          data-cy={`edit-project-row-${project?.id}`}
        >
          <Edit />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={handleOpen}
          startIcon={<ManageHistoryIcon />}
          data-cy="add-project"
        >
          Add Project
        </Button>
      )}
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClick={disablePropagation}
        slotProps={{
          paper: {
            component: "form",
            onSubmit: handleSubmit(onSubmit),
            noValidate: true,
          },
        }}
      >
        <Alert message={error} setMessage={setError} level="error" data-cy="error-alert" />
        <DialogTitleBar title={formTitle} Icon={ManageHistoryIcon} iconColor="secondary" />
        <DialogContent>
          <Grid container spacing={4} sx={{ alignItems: "center" }}>
            <Grid
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <FormStringInput
                control={control}
                label="Name"
                name="name"
                required
                rules={{
                  required: "Please provide a name for this project",
                  maxLength: {
                    value: 255,
                    message: "Product name should be less than 256 characters",
                  },
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormStringInput
                control={control}
                label="Jira ID"
                name="jiraId"
                required
                rules={{
                  required: "Please provide a Jira ID for this project",
                  maxLength: {
                    value: 16,
                    message: "Jira ID should be less than 16 characters",
                  },
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormAutocompleteInput
                control={control}
                label="Status"
                name="status"
                options={statusOptions}
                required
                rules={{
                  required: "Status is required.",
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormStringInput
                control={control}
                label="GL"
                name="gl"
                rules={{
                  maxLength: {
                    value: 255,
                    message: "GL should be less than 256 characters",
                  },
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormStringInput
                control={control}
                label="Budget"
                name="budget"
                type="number"
                units="$"
                required
                rules={{
                  required: "A budget is required for this project.",
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <FormDatePicker control={control} label="Start Date" name="startDate" />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <FormDatePicker control={control} label="End Date" name="endDate" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box sx={{ m: 2, marginRight: "auto" }}>
            {!!project && canDelete && (
              <ConfirmAction
                actionName="Delete"
                objectName="Project"
                handleConfirmation={handleDeleteConfirmation}
                confirmKeyword={project.name}
              />
            )}
          </Box>
          <Box sx={{ m: 2 }}>
            <CancelButton color="secondary" isDirty={isDirty} onClick={handleClose}>
              Cancel
            </CancelButton>
          </Box>
          <Box sx={{ m: 2 }}>
            <Button
              id="submitDeviceVariantButton"
              disabled={loading}
              variant="contained"
              color="secondary"
              loading={loading}
              type="submit"
            >
              {project?.id ? "Update" : "Add"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EngineeringProjectForm;
